import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/button/Button';
import { PopoverContent } from '@/shared/ui/Popover/components/PopoverContent';
import { PopoverRoot } from '@/shared/ui/Popover/components/PopoverRoot';
import { PopoverTrigger } from '@/shared/ui/Popover/components/PopoverTrigger';
import { formFieldSizeVariants } from '../helpers/field-size';
import { selectTriggerStyles } from '../select/helpers/trigger-styles';
import type { CascaderProps } from './helpers';

type SearchableSingleCascaderProps = {
  id?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  disabled: boolean;
  hasError: boolean;
  className?: string;
  size: CascaderProps['size'];
  shouldShowClearButton: boolean;
  onClear: (e: React.MouseEvent<HTMLButtonElement>) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  searchValue: string;
  onSearchValueChange: (value: string) => void;
  onSearchKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder: string;
  displayText: string;
  listboxId?: string;
  activeOptionId?: string;
  popoverContent: React.ReactNode;
};

export const SearchableSingleCascader = ({
  id,
  open,
  onOpenChange,
  disabled,
  hasError,
  className,
  size = 'sm',
  shouldShowClearButton,
  onClear,
  inputRef,
  searchValue,
  onSearchValueChange,
  onSearchKeyDown,
  placeholder,
  displayText,
  listboxId,
  activeOptionId,
  popoverContent,
}: SearchableSingleCascaderProps) => {
  const inputValue = open ? searchValue : displayText;
  const inputPlaceholder = open ? '検索...' : placeholder;

  return (
    <div className={cn('relative w-full', className)}>
      <PopoverRoot open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild disabled={disabled}>
          <div className={cn('relative w-full', className)}>
            <input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-haspopup="listbox"
              aria-expanded={open}
              aria-controls={listboxId}
              aria-activedescendant={activeOptionId}
              disabled={disabled}
              id={id}
              aria-invalid={hasError || undefined}
              autoComplete="off"
              value={inputValue}
              placeholder={inputPlaceholder}
              onChange={(e) => {
                onSearchValueChange(e.target.value);
                if (!open) onOpenChange(true);
              }}
              onKeyDown={onSearchKeyDown}
              className={cn(
                selectTriggerStyles({ height: 'fixed' }),
                'cursor-text',
                formFieldSizeVariants({ size }),
                shouldShowClearButton && 'pr-10',
                !open && !displayText && 'text-text-muted',
              )}
            />
            {!shouldShowClearButton && (
              <ChevronDownIcon className="pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 opacity-50" />
            )}
          </div>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={4}
          className="flex w-auto flex-col bg-surface-base p-2"
          onOpenAutoFocus={(event) => event.preventDefault()}
          onFocusOutside={(event) => event.preventDefault()}
        >
          {popoverContent}
        </PopoverContent>
      </PopoverRoot>
      {shouldShowClearButton && (
        <Button
          variant="ghost"
          size="none"
          onClick={onClear}
          className="absolute top-1/2 right-3 -translate-y-1/2"
          aria-label="選択をクリア"
          tabIndex={-1}
        >
          <XMarkIcon className="text-text-muted" />
        </Button>
      )}
    </div>
  );
};
